import React, { useEffect, useState } from "react"
import { useDispatch, useSelector } from "react-redux"
import { useTheme, withStyles } from "@material-ui/core/styles"
import {
	FormControl,
	makeStyles,
	TextField,
	Button,
	Checkbox,
	FormControlLabel,
	Typography,
	Switch,
	InputLabel,
	MenuItem,
	Select,
} from "@material-ui/core"
import { useFormik } from "formik"
import * as yup from "yup"
import { createCaseRequest, createCaseRequestFailedClear } from "../../actions/bifrostActions"
import { supportedOCRLanguage } from "../../utils/constants"
import { useTranslation } from "react-i18next"
import "./index.css"

const useStyles = makeStyles((theme) => ({
	root: {
		display: "flex",
		flexWrap: "wrap",
	},
	margin: {
		margin: theme.spacing(2),
	},
	withoutLabel: {
		marginTop: theme.spacing(3),
	},
	textField: {
		width: "25ch",
	},
	input: {
		border: "2px solid red",
	},
	twoInputes: {
		display: "flex",
		justifyContent: "space-between",
		flexDirection: "row",
		width: "100%",
		margin: theme.spacing(2),
	},
	numbers: {
		width: "48%",
	},
	formFooterAction: {},
	formActions: {
		display: "flex",
		justifyContent: "flex-end",
		flexDirection: "row",
		width: "100%",
	},
	formActionsBtn: {
		margin: "0.5rem",
		"&:disabled": {
			backgroundColor: "#e0e0e0 !important",
		},
	},
	dividerLine: {
		width: "100%",
		border: `1px solid ${theme.palette.grey[400]}`,
	},
	keepDuplicateSection: {
		margin: theme.spacing(1),
		marginLeft: theme.spacing(2),
	},
	headerTitle: {
		marginLeft: theme.spacing(2),
		marginBottom: theme.spacing(1),
	},
	switch: {
		padding: "5px 0",
		display: "flex",
		alignItems: "flex-start",
		"& .MuiFormControlLabel-label": {
			fontSize: theme.typography.fontSize,
			fontWeight: theme.typography.fontWeightBold,
		},
	},
	inputLabel: { backgroundColor: theme.palette.common.white },
	formControl: {
		margin: theme.spacing(1),
		"& .MuiSelect-icon": {
			color: theme.palette.common.black,
		},
		"& .MuiSelect-outlined": {
			border: `solid 1px ${theme.palette.grey.D400}`,
			borderRadius: "5px",
		},
		"& .MuiOutlinedInput-notchedOutline": {
			border: "transparent",
		},
	},
	hasOCR: {
		marginLeft: `110px !important`,
	},
}))

//form validation
const validationSchema = yup.object({
	name: yup.string("Enter case name").trim().required("Case name is required"),
	description: yup.string("Enter description"),
	chunk_size: yup.number("Enter number").min(0).required("Chunk size is required"),
	chunk_overlap: yup.number("Enter chunk overlap").min(0).required("Chunk overlap is required"),
})

const CreateCase = ({ callback }) => {
	const classes = useStyles()
	const dispatch = useDispatch()
	const [t] = useTranslation("global")
	const theme = useTheme()
	const isLoading = useSelector((state) => state.bifrost.isCaseLoading)
	const isSaved = useSelector((state) => state.bifrost.isSaved)
	const createError = useSelector((state) => state.bifrost.error)

	const [keepFileChecked, setKeepFileChecked] = useState(false)
	const [hasEmbeddedEmail, setHasEmbeddedEmail] = useState(true)
	const [hasOCR, setHasOCR] = useState(true)

	const MenuProps = {
		anchorOrigin: {
			vertical: "bottom",
			horizontal: "left",
		},
		getContentAnchorEl: null,
		PaperProps: {
			style: {
				maxHeight: 270,
			},
		},
	}

	const formik = useFormik({
		initialValues: {
			name: "",
			description: "",
			chunk_size: 2600,
			chunk_overlap: 0,
			delete_duplicate_files_from_disk: !keepFileChecked,
			embbeded_email_txt: true,
			embbeded_email_pdf: true,
			ocr_primary_language: "",
			ocr_secondary_language: "",
			ocr_tertiary_language: "",
			has_ocr: hasOCR,
			ocr_lang: ['nor', 'eng'],
		},
		validationSchema: validationSchema,
		onSubmit: (values) => {
			if (values.has_ocr) {
				if (formik.values.ocr_primary_language !== "") {
					values.ocr_lang.push(formik.values.ocr_primary_language)
					if (formik.values.ocr_secondary_language !== "") {
						values.ocr_lang.push(formik.values.ocr_secondary_language)
						if (formik.values.ocr_tertiary_language !== "") {
							values.ocr_lang.push(formik.values.ocr_tertiary_language)
						}
					}
				}
				delete values.ocr_primary_language
				delete values.ocr_secondary_language
				delete values.ocr_tertiary_language
			}
			dispatch(createCaseRequest(values))
		},
	})

	useEffect(() => {
		if (isSaved) {
			formik.resetForm()
			callback(true)
			dispatch(createCaseRequestFailedClear())
		}
	}, [isSaved, callback, dispatch])

	useEffect(() => {
		return () => {
			dispatch(createCaseRequestFailedClear())
		}
	}, [dispatch])

	const BlueSwitch = withStyles((theme) => ({
		root: {
			marginLeft: "22px",
			switchBase: {
				color: theme.palette.common.white,
				"&$checked": {
					color: theme.palette.primary.dark,
				},
				"&$checked + $track": {
					backgroundColor: theme.palette.primary.dark,
				},
			},
			checked: {},
			track: {},
		},
	}))(Switch)

	const handleKeepDuplicateChange = (event) => {
		formik.setFieldValue("delete_duplicate_files_from_disk", !event.target.checked)
		setKeepFileChecked(event.target.checked)
	}

	const handleChange = (event) => {
		setHasEmbeddedEmail(event.target.checked)
		formik.setFieldValue("embbeded_email_pdf", false)
		formik.setFieldValue("embbeded_email_txt", false)
	}

	const handleChangeOCR = (event) => {
		setHasOCR(event.target.checked)
		formik.setFieldValue("has_ocr", event.target.checked)
		formik.setFieldValue("ocr_secondary_language", "")
		formik.setFieldValue("ocr_tertiary_language", "")
		formik.setFieldValue("ocr_primary_language", "")
	}

	return (
		<>
			<form className={classes.root} noValidate autoComplete="off" id="create-case" onSubmit={formik.handleSubmit}>
				{/* <div className='form-content'> */}
				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="name"
						label="Case name*"
						variant="outlined"
						value={formik.values.name}
						onChange={formik.handleChange}
						error={formik.touched.name && Boolean(formik.errors.name)}
						helperText={formik.touched.name && formik.errors.name}
					/>
				</FormControl>

				<FormControl fullWidth className={classes.margin}>
					<TextField
						id="description"
						label="Description"
						multiline
						rows={4}
						variant="outlined"
						value={formik.values.description}
						onChange={formik.handleChange}
						error={formik.touched.description && Boolean(formik.errors.description)}
						helperText={formik.touched.description && formik.errors.description}
					/>
				</FormControl>
				<hr className={classes.dividerLine} />
				<Typography style={{ ...theme.typography.subTitle1 }} className={classes.headerTitle}>
					{t("bifrost.createCase.advanceConfiguration")}
				</Typography>
				<FormControl className={classes.twoInputes}>
					<TextField
						id="chunksize"
						label="Chunk size*"
						type="number"
						variant="outlined"
						className={classes.numbers}
						value={formik.values.chunk_size}
						//onChange={formik.handleChange}
						onChange={(e) => {
							const { value } = e.target
							formik.setFieldValue("chunk_size", value)
						}}
						error={formik.touched.chunk_size && Boolean(formik.errors.chunk_size)}
						helperText={formik.touched.chunk_size && formik.errors.chunk_size}
					/>
					<TextField
						id="chunkoverlap"
						label="Chunk overlap*"
						type="number"
						variant="outlined"
						className={classes.numbers}
						value={formik.values.chunk_overlap}
						//onChange={formik.handleChange}
						onChange={(e) => {
							const { value } = e.target
							formik.setFieldValue("chunk_overlap", value)
						}}
						error={formik.touched.chunk_overlap && Boolean(formik.errors.chunk_overlap)}
						helperText={formik.touched.chunk_overlap && formik.errors.chunk_overlap}
					/>
				</FormControl>
				<FormControl fullWidth className={classes.keepDuplicateSection}>
					<FormControlLabel
						control={
							<Checkbox checked={keepFileChecked} onChange={handleKeepDuplicateChange} name="keepFiles" color="primary" />
						}
						label={t("bifrost.createCase.keepFiles")}
					/>
				</FormControl>
				<FormControl fullWidth className={classes.switch}>
					<FormControlLabel
						control={<BlueSwitch checked={hasEmbeddedEmail} onChange={handleChange} name="hasEmbeddedEmail" />}
						label={t("bifrost.createCase.hasEmbeddedEmail")}
						labelPlacement="start"
					/>
				</FormControl>
				{hasEmbeddedEmail && (
					<>
						<FormControl className={classes.keepDuplicateSection}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formik.values.embbeded_email_pdf.check}
										value={formik.values.embbeded_email_pdf.check}
										onChange={(e) => {
											const { value } = e.target
											formik.setFieldValue("embbeded_email_pdf", !formik.values.embbeded_email_pdf)
										}}
										name="hasEmbedded"
										color="primary"
									/>
								}
								label={t("bifrost.createCase.pdf")}
							/>
						</FormControl>
						<FormControl className={classes.keepDuplicateSection}>
							<FormControlLabel
								control={
									<Checkbox
										checked={formik.values.embbeded_email_txt.check}
										value={formik.values.embbeded_email_txt.check}
										onChange={(e) => {
											const { value } = e.target
											formik.setFieldValue("embbeded_email_txt", !formik.values.embbeded_email_txt.check)
										}}
										name="hasEmbedded"
										color="primary"
									/>
								}
								label={t("bifrost.createCase.txt")}
							/>
						</FormControl>
					</>
				)}

				<FormControl fullWidth className={classes.switch}>
					<FormControlLabel
						control={<BlueSwitch className={classes.hasOCR} checked={hasOCR} onChange={handleChangeOCR} name="hasOCR" />}
						label={t("bifrost.createCase.hasOCR")}
						labelPlacement="start"
					/>
				</FormControl>
				{hasOCR && (
					<>
						<FormControl variant="outlined" fullWidth className={classes.formControl}>
							<InputLabel className={classes.inputLabel}>{t("bifrost.createCase.primaryLanguage")}</InputLabel>
							<Select
								id="file-type-select-label"
								variant="outlined"
								value={formik.values.ocr_primary_language}
								MenuProps={MenuProps}
								onChange={(e) => {
									const { value } = e.target
									formik.setFieldValue("ocr_primary_language", value)
									formik.setFieldValue("ocr_secondary_language", "")
									formik.setFieldValue("ocr_tertiary_language", "")
								}}
							>
								{supportedOCRLanguage?.map((OCRLanguage) => {
									return <MenuItem value={OCRLanguage?.value}>{OCRLanguage?.label}</MenuItem>
								})}
							</Select>
						</FormControl>
						<FormControl variant="outlined" fullWidth className={classes.formControl}>
							<InputLabel className={classes.inputLabel}>{t("bifrost.createCase.secondaryLanguage")}</InputLabel>
							<Select
								variant="outlined"
								MenuProps={MenuProps}
								label={t("bifrost.createCase.secondaryLanguage")}
								disabled={formik.values.ocr_primary_language === ""}
								value={formik.values.ocr_secondary_language}
								onChange={(e) => {
									const { value } = e.target
									formik.setFieldValue("ocr_secondary_language", value)
									formik.setFieldValue("ocr_tertiary_language", "")
								}}
							>
								{supportedOCRLanguage
									.filter((v) => ![formik.values.ocr_primary_language].includes(v?.value))
									?.map((OCRLanguage) => {
										return <MenuItem value={OCRLanguage?.value}>{OCRLanguage?.label}</MenuItem>
									})}
							</Select>
						</FormControl>
						<FormControl variant="outlined" fullWidth className={classes.formControl}>
							<InputLabel className={classes.inputLabel}>{t("bifrost.createCase.tertiaryLanguage")}</InputLabel>
							<Select
								variant="outlined"
								MenuProps={MenuProps}
								label={t("bifrost.createCase.tertiaryLanguage")}
								value={formik.values.ocr_tertiary_language}
								disabled={formik.values.ocr_secondary_language === ""}
								onChange={(e) => {
									const { value } = e.target
									formik.setFieldValue("ocr_tertiary_language", value)
								}}
							>
								{supportedOCRLanguage
									.filter(
										(v) =>
											![formik.values.ocr_secondary_language, formik.values.ocr_primary_language].includes(v?.value)
									)
									?.map((OCRLanguage) => {
										return <MenuItem value={OCRLanguage?.value}>{OCRLanguage?.label}</MenuItem>
									})}
							</Select>
						</FormControl>
					</>
				)}
				<hr className={classes.dividerLine} />
				{createError && (
					<Typography color="error" style={{ marginLeft: 16, marginBottom: 8 }}>
						{typeof createError === "string"
							? createError
							: createError?.data?.message || createError?.message || "Failed to create case. Please try again."}
					</Typography>
				)}

				<FormControl className={classes.formActions}>
					<Button
						variant="contained"
						classes={classes}
						className={classes.formActionsBtn}
						style={{
							textTransform: "initial",
							fontSize: "14px",
							padding: "8px 12px",
						}}
						onClick={() => {
							dispatch(createCaseRequestFailedClear())
							callback(false)
						}}
					>
						{t("bifrost.buttonTexts.cancel")}
					</Button>
					<Button
						variant="contained"
						color="primary"
						classes={classes}
						className={classes.formActionsBtn}
						disabled={hasOCR ? formik.values.ocr_primary_language === "" : false}
						style={{
							textTransform: "initial",
							fontSize: "14px",
							padding: "8px 12px",
							backgroundColor: "#171D90",
						}}
						type="submit"
					>
						{isLoading ? "Creating..." : "Create case"}
					</Button>
				</FormControl>
				{/* </div> */}
			</form>
		</>
	)
}

export default CreateCase
